import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import bannerLogo from '@assets/images/presentation/banner-log.svg';
import logo from '@assets/images/presentation/logo.svg';
import designLab from '@assets/images/presentation/design-lab.svg';
import bottomSlogan from '@assets/images/presentation/slogan.svg';

import CommonImage from '@components/CommonImage';
import ModalContainer from '@components/modals/ModalContainer';
import NoData from '@components/NoData';
import { toastSuccess } from '@components/Toast';

import UploadPresentationImages from '@features/collection/components/UploadPresentationImages';
import EditPresentationInfo from '@features/collection/components/EditPresentionInfo';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	addPresentation,
	fetchPresentationInfo,
	getPresentationLink,
	savePresentationSequence
} from '@services/presentationService';

import { printApiError } from '@utils/Util';

import { getBuyerAppBaseFrontendUrl } from '@/constants/constant';

import { useGetPresentationPSDFilesMutation } from '@/api/commonApis';

import lastSection from '../../../assets/images/presentation/last-section-bg.png';

const CollectionPresentation = ({
	isPlay,
	collectionName,
	collectionId,
	onCLoseModal,
	entityType = 'COLLECTION',
	selectedProductIds = [],
	isShowPSD = false
}) => {
	const [collectionInfo, setCollectionInfo] = useState([]);
	const [presentationInfo, setPresentationInfo] = useState({});
	const [editModal, setEditModal] = useState(false);
	const [link, setLink] = useState('');
	const [isLinkClicked, setIsLinkClicked] = useState(false);
	const [isClickSerial, setIsClickSerial] = useState(false);
	const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
	const linkRef = useRef();
	const [presentationId, setPresentationId] = useState(null);
	const dispatch = useDispatch();

	const [getPresentationPSDFiles, { data: psdFiles }] = useGetPresentationPSDFilesMutation();

	useEffect(() => {
		if (isShowPSD) {
			getPresentationPSDFiles({
				body: selectedProductIds
			});
		}
	}, [isShowPSD]);

	const handleAddPresentation = async () => {
		const requestBody = {
			entityId: collectionId,
			entityType: entityType,
			orderedProductIds: selectedProductIds
		};
		const response = await addPresentation(requestBody);
		setPresentationId(response);
		return response;
	};

	useEffect(() => {
		if (isPlay) {
			handleAddPresentation();
		}
	}, [collectionId, entityType, selectedProductIds.length, isPlay]);

	const getPresentationInfo = async () => {
		try {
			dispatch(openLoader());
			if (presentationId) {
				const response = await fetchPresentationInfo(presentationId);
				setPresentationInfo({
					collectionId: collectionId,
					market: response.market,
					season: response.season,
					brand: response.brand,
					middleDocument: response.middleDocument,
					endDocument: response.endDocument,
					coverDocument: response.coverDocument,
					presentationName: response?.presentationName
				});
				setCollectionInfo(response?.productInfoForPresentationList);
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onSavePresentationSequence = async (productIds) => {
		try {
			dispatch(openLoader());
			const requestBody = {
				entityId: collectionId,
				orderedProductIds: productIds,
				entityType: entityType
			};
			const response = await savePresentationSequence(requestBody, presentationId);
			if (response?.success) {
				getPresentationInfo();
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (isPlay && presentationId) {
			getPresentationInfo();
		}
	}, [isPlay, presentationId]);

	const reorder = (list, startIndex, endIndex) => {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);
		return result;
	};

	const onDragEnd = (e) => {
		if (!e.destination) {
			return;
		}
		const sorted = reorder(collectionInfo, e.source.index, e.destination.index);
		setCollectionInfo(sorted);
		const productIds = sorted?.map((item) => item.id);
		onSavePresentationSequence(productIds);
	};

	const handleCreatePresentation = async () => {
		try {
			dispatch(openLoader());
			const response = await getPresentationLink(presentationId);
			setIsLinkClicked(false);
			const link = getBuyerAppBaseFrontendUrl() + '/presentation?presentationId=' + response;
			setIsLinkClicked(true);
			setLink(link);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onClickSerial = () => setIsClickSerial(!isClickSerial);

	return (
		<>
			{isPlay && (
				<ModalContainer
					onCloseModal={onCLoseModal}
					className="design-view-modal presentation-play-modal"
				>
					<div className="modal-dialog" role="document">
						<div className="modal-content">
							<div className="modal-header">
								<nav className="navbar navbar-expand-lg navbar-light design-tool-header">
									<div className="toggle-menu-btn">
										<button
											className="button text serial-btn pr-0"
											onClick={onClickSerial}
										>
											<CommonImage src="/icons/up-down-arrow.svg" alt="serial" />
											<span className="text-white ml-1 text-underline">Serial</span>
										</button>
									</div>
									<div className="design-selection-dropdown one-third-item">
										<div className="dropdown-btn d-flex align-items-center">
											<span className="regular-14">
												{presentationInfo?.presentationName || collectionName}
											</span>
											<button
												className="button ml-1 p-0 text-underline"
												onClick={() => setIsOpenUploadModal(true)}
											>
												<CommonImage
													src="/icons/edit-pencil-white.svg"
													alt="presentation"
													className="mr-1"
												/>
												Edit presentation
											</button>
										</div>
									</div>
									<div className="profile-and-comments one-third-item">
										<ul className="d-flex flex-wrap menus">
											<li>
												<div>
													<button
														className="button text size32 p-0"
														type="button"
														onClick={handleCreatePresentation}
														disabled={collectionInfo.length === 0}
													>
														<CommonImage src="/icons/copy.svg" alt="share" />
														<span className="text-white ml-2">
															Copy Presentation Link
														</span>
													</button>
													<div
														ref={linkRef}
														className={
															!isLinkClicked
																? 'copy-url-popup hidden'
																: 'copy-url-popup'
														}
													>
														<div className="copy-url">
															<p className="regular-14 gray_dark_01 mb-3 weight-500">
																Copy link to share with buyer
															</p>
															<div className="d-flex">
																<input
																	type="text"
																	autoComplete="off"
																	value={link}
																	disabled
																/>
																<button
																	className="button primary size36"
																	onClick={() => {
																		navigator.clipboard.writeText(link);
																		toastSuccess('Copied');
																		setIsLinkClicked(false);
																	}}
																>
																	Copy
																</button>
															</div>
														</div>
													</div>
												</div>
											</li>

											<li>
												<button
													type="button"
													className="button text p-0"
													data-dismiss="modal"
													aria-label="Close"
													onClick={onCLoseModal}
												>
													<CommonImage
														src="/icons/Close-white.svg"
														className="cursor-pointer"
														alt="close"
													/>
												</button>
											</li>
										</ul>
									</div>
								</nav>
							</div>
							{collectionInfo.length === 0 ? (
								<div className="no-presentaion-available">
									<NoData message={'No presentation available'} />
								</div>
							) : (
								<div className="modal-body">
									{isClickSerial && (
										<>
											<DragDropContext onDragEnd={onDragEnd}>
												<Droppable droppableId="Tbody">
													{(provided) => (
														<div
															className="dragable-design-item"
															{...provided.droppableProps}
															ref={provided.innerRef}
														>
															<div className="draggable-all">
																<div className="draggable-items-container scroll-y-label">
																	{collectionInfo?.map((item, index) => (
																		<Draggable
																			key={item.id}
																			draggableId={item.id.toString()}
																			index={index}
																		>
																			{(provided) => (
																				<div
																					className="draggable-single-item"
																					key={index}
																					ref={provided.innerRef}
																					{...provided.draggableProps}
																					{...provided.dragHandleProps}
																				>
																					<CommonImage
																						className="cursor-pointer"
																						src={
																							item?.frontImageUrl
																						}
																						isStatic={false}
																						alt="front"
																					/>
																				</div>
																			)}
																		</Draggable>
																	))}
																	{provided.placeholder}
																</div>
															</div>
														</div>
													)}
												</Droppable>
											</DragDropContext>
										</>
									)}
									<div className="modal-artboards scroll-y-label">
										<section
											className={`into-section-v2 ${
												presentationInfo?.brand?.length < 8 ? 'large' : ''
											}`}
										>
											{/* <h1 className="brand-name-lebel">{presentationInfo?.brand}</h1>
											<div className="intro-cover">
												{presentationInfo?.coverDocument?.docUrl ? (
													<CommonImage
														src={presentationInfo?.coverDocument?.docUrl}
														alt="cover"
														className="cover-photo"
														isStatic={false}
													/>
												) : (
													<div className="cover-photo-container"></div>
												)}
												<div className="presentation-edit-category d-flex justify-content-between">
													<button
														className="button ml-3"
														onClick={() => setIsOpenUploadModal(true)}
													>
														<CommonImage
															src="/icons/edit-pencil-white.svg"
															alt="presentation"
															className="mr-1"
														/>
														Edit presentation
													</button>

													{presentationInfo?.market && presentationInfo?.season && (
														<div className="cat-info">
															<p>
																{presentationInfo?.market}:{' '}
																<span> {presentationInfo?.season}</span>
															</p>
														</div>
													)}
												</div>
											</div>
											<CommonImage
												src="/images/nitex-presentation-logo.svg"
												alt="logo"
												className="bottom-logo"
											/> */}
											<div
												className="presentation-banner d-flex flex-column justify-content-between"
												style={{
													backgroundImage: `url(${presentationInfo?.coverDocument?.docUrl})`
												}}
											>
												<div className="presentation-line-and-content">
													<div className="border-top-line d-flex justify-content-between align-items-start">
														<p className="title left-title">
															{(presentationInfo?.market ||
																presentationInfo?.season) && (
																<span className="text-dark">
																	{presentationInfo?.season}{' '}
																	{presentationInfo?.market}
																</span>
															)}
														</p>

														<div>
															{/** TODO: ADD category */}
															<p className="title sub-right-title text-start">
																{presentationInfo?.presentationName && (
																	<span className="text-dark">
																		{presentationInfo?.presentationName}
																	</span>
																)}
															</p>
															<p className="title right-title"></p>
														</div>
													</div>
												</div>
												<div className="middle-content text-center">
													<CommonImage
														className="logo-image"
														src={logo}
														alt="logo"
														isStatic={false}
													/>
													<span className="x-symbol d-block py-3 regular-16 weight-600">
														x
													</span>
													<p className="brand-name weight-600">
														{presentationInfo?.brand
															? presentationInfo?.brand?.toUpperCase()
															: ''}
													</p>
												</div>
												<div className="presentation-line-and-content bottom-line-and-content">
													<div className="border-top-line d-flex justify-content-between align-items-center">
														<p className="title left-title text-neon text-lowercase">
															<Link to="https://www.nitex.com" target="_blank">
																www.nitex.com
															</Link>
														</p>
														<p className="title right-title">
															<CommonImage
																src={bannerLogo}
																alt="logo"
																isStatic={false}
															/>
														</p>
													</div>
												</div>
											</div>
										</section>
										{presentationInfo?.middleDocument?.docUrl && (
											<div className="single-design-view-page">
												<div className="presentation-view-container">
													<div className="full-page-photo">
														<CommonImage
															src={presentationInfo?.middleDocument?.docUrl}
															alt="cover"
															isStatic={false}
														/>
													</div>
												</div>
											</div>
										)}
										{collectionInfo?.map((item) => (
											<div className="single-design-view-page" key={item.id}>
												<div className="presentation-line-and-content">
													<div className="border-top-line black-line d-flex justify-content-between align-items-center">
														<div className="d-flex align-items-center">
															<p className="title left-title text-black">
																{' '}
																{presentationInfo?.season}
															</p>
															<p className="title left-sub-title text-black">
																{item?.marketName}
															</p>
														</div>

														<div>
															<p className="title sub-right-title text-start text-black">
																{item?.category}
															</p>
															<p className="title right-title"></p>
														</div>
													</div>
												</div>
												{/* {item?.setPackStyleFlag ===
													STATIC_SET_CONSTANTS.SET_MEMBER && (
													<div className="mb-2">
														<span
															className={`status ${
																item?.setPackStyleFlag ===
																STATIC_SET_CONSTANTS.PACK_PRODUCT
																	? 'pack'
																	: item?.setPackStyleFlag ===
																		  STATIC_SET_CONSTANTS.SET_MEMBER
																		? 'set'
																		: ''
															}`}
														>
															{item?.setPackStyleFlag ===
															STATIC_SET_CONSTANTS.PACK_PRODUCT
																? 'Pack'
																: item?.setPackStyleFlag ===
																	  STATIC_SET_CONSTANTS.SET_MEMBER
																	? 'Set'
																	: ''}
														</span>
														<span className="regular-12 weight-500 ml-2">
															{item?.setPackName}
														</span>
													</div>
												)} */}
												<div className="presentation-view-container position-relative">
													<div className="images-view-section">
														<div className="one-third full-photo">
															<CommonImage
																src={item.frontImageUrl}
																alt="front"
																isStatic={false}
															/>
														</div>
														<div className="one-third full-photo">
															<CommonImage
																src={item.backImageUrl}
																alt="back"
																isStatic={false}
															/>
														</div>
														<div className="one-third fabric-photo">
															<div className="single-fabric">
																<CommonImage
																	src={item.fabricImageUrl}
																	alt="fabric"
																	isStatic={false}
																/>
															</div>
															<div className="single-fabric">
																<CommonImage
																	src={item?.keyDetailsImageUrl}
																	alt="keyDetails"
																	isStatic={false}
																/>
															</div>
															<div className="single-design-details">
																<div className="item-details">
																	<p className="regular-14">
																		<span className="label-text">
																			Style:
																		</span>
																		<span className="bold">
																			{item.referenceNumber}
																		</span>
																	</p>
																	{item?.fabric2 ? (
																		<>
																			<div className="fabric-and-weight-item d-flex">
																				<p className="regular-14">
																					<span className="label-text">
																						Fabric 1:
																					</span>
																					<span>
																						{item.fabric1},{' '}
																						<span>
																							{item.weight1}
																						</span>
																					</span>
																				</p>
																			</div>

																			<div className="fabric-and-weight-item d-flex">
																				<p className="regular-14">
																					<span className="label-text">
																						Fabric 2:
																					</span>
																					<span>
																						{item.fabric2},{' '}
																						<span>
																							{item.weight2}
																						</span>
																					</span>
																				</p>
																			</div>
																		</>
																	) : (
																		<>
																			<p className="regular-14">
																				<span className="label-text">
																					Fabric:
																				</span>
																				<span>
																					{item.fabric1},{' '}
																					<span>
																						{item.weight1}
																					</span>
																				</span>
																			</p>
																		</>
																	)}
																</div>
															</div>

															{isShowPSD &&
																psdFiles &&
																psdFiles[item?.id] &&
																psdFiles[item?.id]?.map((file) => (
																	<div
																		key={file?.id}
																		className="d-flex items-center"
																	>
																		<span className="chip-square mr-1 mb-2 mr-2">
																			{file?.name.replace('.psd', '')}
																		</span>
																		<CommonImage
																			src="/icons/download-gray-dark-02.svg"
																			alt="download"
																			width={16}
																			height={16}
																			className="cursor-pointer"
																			onClick={() =>
																				window.open(
																					file?.docUrl,
																					'_blank'
																				)
																			}
																		/>
																	</div>
																))}
														</div>
													</div>

													{/* <div className="single-design-details">
														<div className="design-details-heading">
															<p>
																<span className="bold">NITEX</span> DESIGNLAB
															</p>

															<p>
																STYLE:{' '}
																<span className="bold">
																	{item?.referenceNumber}
																</span>
															</p>
														</div>
														<div className="design-details-body">
															<div className="item-details">
																<p>
																	<span>ITEM:</span>
																	<span className="bold">
																		{item.styleName}
																	</span>
																</p>
																{item?.fabric2 ? (
																	<>
																		<div className="fabric-and-weight-item d-flex">
																			<p>
																				<span>Fabric:</span>
																				<span className="bold">
																					{item.fabric1}
																				</span>
																			</p>
																			<p className="ml-3">
																				<span className="weight">
																					Weight:
																				</span>
																				<span className="bold">
																					{item.weight1}
																				</span>
																			</p>
																		</div>

																		<div className="fabric-and-weight-item d-flex">
																			<p>
																				<span>Fabric:</span>
																				<span className="bold">
																					{item.fabric2}
																				</span>
																			</p>
																			<p className="ml-3">
																				<span className="weight">
																					Weight:
																				</span>
																				<span className="bold">
																					{item.weight2}
																				</span>
																			</p>
																		</div>
																	</>
																) : (
																	<>
																		<p>
																			<span>Fabric:</span>
																			<span className="bold">
																				{item.fabric1}
																			</span>
																		</p>
																		<p>
																			<span>Weight:</span>
																			<span className="bold">
																				{item.weight1}
																			</span>
																		</p>
																	</>
																)}
															</div>
														</div>
													</div> */}
													{/* down line*/}
													<div className="presentation-line-and-content bottom-line-and-content">
														<div className="border-top-line black-line d-flex justify-content-between align-items-center w-100">
															<p className="title left-title text-black text-lowercase">
																<Link
																	to="https://www.nitex.com"
																	target="_blank"
																>
																	www.nitex.com
																</Link>
															</p>
														</div>
													</div>
													<div className="productionlab right">
														<CommonImage
															src={designLab}
															alt="logo"
															isStatic={false}
														/>
													</div>
												</div>
											</div>
										))}
										{presentationInfo?.endDocument?.docUrl && (
											<div className="single-design-view-page">
												<div className="presentation-view-container">
													<div className="full-page-photo">
														<CommonImage
															src={presentationInfo?.endDocument?.docUrl}
															alt="cover"
															isStatic={false}
														/>
													</div>
												</div>
											</div>
										)}
										<section className="last-section-v2">
											<div
												className="presentation-banner d-flex flex-column justify-content-between"
												style={{
													backgroundImage: `url(${lastSection})`,
													backgroundPosition: '50% 30%'
												}}
											>
												<div className="presentation-line-and-content">
													<div className="border-top-line d-flex justify-content-between align-items-start">
														<p className="title left-title">
															{(presentationInfo?.market ||
																presentationInfo?.season) && (
																<span className="text-dark">
																	{presentationInfo?.season}{' '}
																	{presentationInfo?.market}
																</span>
															)}
														</p>
														<div>
															<p className="title sub-right-title text-start">
																{presentationInfo?.presentationName && (
																	<span className="text-dark">
																		{presentationInfo?.presentationName}
																	</span>
																)}
															</p>
															<p className="title right-title"></p>
														</div>
													</div>
												</div>
												<div className="middle-content text-center">
													<CommonImage
														src={bottomSlogan}
														alt="logo"
														isStatic={false}
														className="bottom-slogan"
													/>
												</div>
												<div className="presentation-line-and-content bottom-line-and-content">
													<div className="border-top-line d-flex justify-content-between align-items-center">
														<p className="title left-title text-neon text-lowercase">
															<Link to="https://www.nitex.com" target="_blank">
																www.nitex.com
															</Link>
														</p>
														<p className="title right-title">
															<CommonImage
																src={bannerLogo}
																alt="logo"
																isStatic={false}
															/>
														</p>
													</div>
												</div>
											</div>
										</section>
									</div>
								</div>
							)}
						</div>
					</div>
				</ModalContainer>
			)}
			{/* {startLoader && <TimeBasedLoader startLoader={startLoader} />} */}
			{editModal && (
				<EditPresentationInfo
					isDownLoad={editModal}
					onClose={() => setEditModal(false)}
					collectionTitle={collectionName}
					collectionId={collectionId}
					presentationInfo={presentationInfo}
					setPresentationInfo={setPresentationInfo}
					entityType={entityType}
					presentationId={presentationId}
				/>
			)}
			{isOpenUploadModal && (
				<UploadPresentationImages
					onClose={() => setIsOpenUploadModal(false)}
					title="Edit Presentation"
					isEdit={true}
					collectionInfo={{
						collectionId: collectionId,
						approvalStatus: 'APPROVED'
					}}
					callBack={getPresentationInfo}
					entityType={entityType}
					presentationId={presentationId}
				/>
			)}
		</>
	);
};

export default CollectionPresentation;
